/**
 * Explore Page — 探索目的地
 *
 * 功能：POI搜索、酒店搜索、景点搜索、攻略搜索
 * 技术：OpenSearch V2 API + TanStack Query
 */

import { useState } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Search, MapPin, Hotel, Camera, BookOpen, Star, Loader2 } from 'lucide-react';
import { useSearchPois, useSearchHotels, useSearchAttractions, useSearchGuides } from '@/hooks/useSearch';
import { SearchResultCard } from '@/components/search/SearchResultCard';
import { useSearchStore } from '@/store/searchStore';

const CITIES = ['首尔', '东京', '曼谷', '新加坡', '巴厘岛', '巴黎', '伦敦', '纽约'];
const CATEGORIES = ['全部', '美食', '景点', '购物', '文化', '自然', '亲子'];

const Explore = () => {
  const { query, setQuery, activeTab, setActiveTab, addRecentSearch } = useSearchStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('全部');
  const [hasSearched, setHasSearched] = useState(false);

  // Search hooks
  const poisQuery = useSearchPois(
    { query: searchQuery, city: selectedCity, category: selectedCategory === '全部' ? undefined : selectedCategory, size: 20 },
    hasSearched
  );

  const hotelsQuery = useSearchHotels(
    { query: searchQuery, city: selectedCity, size: 20 },
    hasSearched
  );

  const attractionsQuery = useSearchAttractions(
    { query: searchQuery, city: selectedCity, size: 20 },
    hasSearched
  );

  const guidesQuery = useSearchGuides(
    { query: searchQuery, city: selectedCity, size: 20 },
    hasSearched
  );

  const handleSearch = () => {
    if (!searchQuery.trim() && !selectedCity) return;
    setQuery(searchQuery);
    addRecentSearch(searchQuery);
    setHasSearched(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const renderSearchResults = (queryResult, type, emptyMessage) => {
    if (!hasSearched) {
      return (
        <div className="text-center py-16 text-gray-400">
          <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>输入关键词开始探索世界</p>
        </div>
      );
    }

    if (queryResult.isLoading) {
      return (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <span className="ml-2 text-gray-500">搜索中...</span>
        </div>
      );
    }

    if (queryResult.isError) {
      return (
        <div className="text-center py-16 text-gray-400">
          <p>搜索服务暂时不可用</p>
          <p className="text-xs mt-1">请稍后重试</p>
        </div>
      );
    }

    const data = queryResult.data;
    if (!data || !data.items || data.items.length === 0) {
      return (
        <div className="text-center py-16 text-gray-400">
          <p>{emptyMessage || '暂无搜索结果'}</p>
          <p className="text-xs mt-1">试试其他关键词</p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between text-sm text-gray-400 mb-4">
          <span>找到 {data.total} 个结果 ({data.tookMs}ms)</span>
          <span className="text-xs">来源: {data.source}</span>
        </div>
        {data.items.map((item, idx) => (
          <SearchResultCard key={idx} type={type} data={item} />
        ))}
        {data.hasNext && (
          <Button variant="outline" className="w-full mt-4">加载更多</Button>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-bg-primary">
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 md:px-6 pt-24 pb-20">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-text-primary mb-2">探索目的地</h1>
          <p className="text-text-secondary">搜索全球 POI、酒店、景点和攻略</p>
        </div>

        {/* Search Bar */}
        <div className="flex gap-2 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              placeholder="搜索地点、酒店、景点..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className="pl-12 h-12 rounded-xl border-gray-200 bg-white"
            />
          </div>
          <Button onClick={handleSearch} className="bg-primary hover:bg-primary-hover text-white rounded-xl px-8">
            搜索
          </Button>
        </div>

        {/* City Filter */}
        <div className="flex flex-wrap gap-2 mb-4">
          <span className="text-sm text-gray-500 py-1">热门城市：</span>
          {CITIES.map(city => (
            <Badge
              key={city}
              onClick={() => { setSelectedCity(city); setHasSearched(true); }}
              className={`cursor-pointer rounded-full transition-colors ${
                selectedCity === city
                  ? 'bg-primary text-white'
                  : 'bg-white text-text-secondary hover:bg-primary/10'
              }`}
            >
              {city}
            </Badge>
          ))}
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-6">
          <span className="text-sm text-gray-500 py-1">分类：</span>
          {CATEGORIES.map(cat => (
            <Badge
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`cursor-pointer rounded-full transition-colors ${
                selectedCategory === cat
                  ? 'bg-primary text-white'
                  : 'bg-white text-text-secondary hover:bg-primary/10'
              }`}
            >
              {cat}
            </Badge>
          ))}
        </div>

        {/* Results Tabs */}
        <Tabs defaultValue="pois" className="w-full">
          <TabsList className="mb-6 bg-white rounded-xl p-1">
            <TabsTrigger value="pois" className="rounded-lg">
              <MapPin className="w-4 h-4 mr-1" /> POI
            </TabsTrigger>
            <TabsTrigger value="hotels" className="rounded-lg">
              <Hotel className="w-4 h-4 mr-1" /> 酒店
            </TabsTrigger>
            <TabsTrigger value="attractions" className="rounded-lg">
              <Camera className="w-4 h-4 mr-1" /> 景点
            </TabsTrigger>
            <TabsTrigger value="guides" className="rounded-lg">
              <BookOpen className="w-4 h-4 mr-1" /> 攻略
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pois">
            {renderSearchResults(poisQuery, 'poi', '暂无 POI 结果')}
          </TabsContent>

          <TabsContent value="hotels">
            {renderSearchResults(hotelsQuery, 'hotel', '暂无酒店结果')}
          </TabsContent>

          <TabsContent value="attractions">
            {renderSearchResults(attractionsQuery, 'attraction', '暂无景点结果')}
          </TabsContent>

          <TabsContent value="guides">
            {renderSearchResults(guidesQuery, 'guide', '暂无攻略结果')}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Explore;
